import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lightbulb } from "lucide-react";
import { absUrl } from "@/lib/api";

/**
 * Garage lobby. Photoreal backdrop + Lights toggle. If the user has picked a
 * "Featured" vehicle, Vinny's rendered image of that car is dropped into the
 * bay with a subtle animated float. Nothing else — no rotate, no paint cycle.
 */
export default function GarageHero({ featured }) {
    const [lightsOn, setLightsOn] = useState(true);
    const featuredUrl = featured?.featured_render_url
        ? absUrl(featured.featured_render_url)
        : null;

    return (
        <div
            className="relative overflow-hidden"
            data-testid="garage-hero"
            style={{
                height: "clamp(240px, 40vh, 380px)",
                background: "#050505",
                borderBottom: "1px solid #262626",
            }}
        >
            {/* Photoreal backdrop */}
            <div
                className="absolute inset-0"
                style={{
                    backgroundImage: "url(/brand/garage-bay.png)",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    filter: lightsOn
                        ? "brightness(1)"
                        : "brightness(0.35) saturate(0.6)",
                    transition: "filter 0.6s ease",
                }}
                aria-hidden
            />

            {/* Featured vehicle render — dropped into the bay */}
            <AnimatePresence>
                {featuredUrl && (
                    <motion.img
                        key={featuredUrl}
                        src={featuredUrl}
                        alt={
                            featured
                                ? `${featured.year} ${featured.model_name} ${featured.trim}`
                                : "Featured ride"
                        }
                        initial={{ opacity: 0, scale: 1.02 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                        className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
                        style={{
                            filter: lightsOn
                                ? "none"
                                : "brightness(0.4) saturate(0.6)",
                            transition: "filter 0.6s ease",
                        }}
                        draggable={false}
                    />
                )}
            </AnimatePresence>

            {/* Subtle animated float overlay — the "animated form" of the image */}
            {featuredUrl && (
                <motion.div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        backgroundImage: `url(${featuredUrl})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        mixBlendMode: "screen",
                        opacity: 0.12,
                    }}
                    animate={{ y: [0, -3, 0], opacity: [0.08, 0.14, 0.08] }}
                    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                />
            )}

            {/* Vignette so overlaid UI reads */}
            <div
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 30%, rgba(0,0,0,0) 55%, rgba(0,0,0,0.75) 100%)",
                }}
            />

            {/* Red ceiling-strip pulse */}
            <motion.div
                aria-hidden
                className="absolute left-0 right-0 top-0 h-[2px] pointer-events-none"
                animate={{
                    opacity: lightsOn ? [0.35, 0.85, 0.35] : 0.05,
                }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                style={{
                    background:
                        "linear-gradient(90deg, transparent 0%, #E10600 30%, #ff5f56 50%, #E10600 70%, transparent 100%)",
                    filter: "blur(1px)",
                }}
            />

            {/* Featured plate — bottom-left */}
            {featured && (
                <div
                    className="absolute left-3 bottom-3 px-3 py-1.5 text-[10px] font-bold text-white pointer-events-none"
                    style={{
                        background: "rgba(6,6,6,0.7)",
                        border: "1px solid #262626",
                        borderRadius: 4,
                        letterSpacing: "0.2em",
                        backdropFilter: "blur(8px)",
                        fontFamily: "'JetBrains Mono', monospace",
                    }}
                >
                    FEATURED · {featured.year} {String(featured.model_name).toUpperCase()}
                </div>
            )}

            {/* Lights toggle — bottom-right */}
            <motion.button
                whileTap={{ scale: 0.94 }}
                whileHover={{ x: -2 }}
                onClick={() => setLightsOn((v) => !v)}
                data-testid="ctrl-lights"
                className="absolute right-3 bottom-3 flex items-center gap-2 px-3 py-2 text-[10px] font-bold text-white"
                style={{
                    background: lightsOn
                        ? "linear-gradient(180deg, #ff3b33 0%, #E10600 55%, #7a0400 100%)"
                        : "rgba(10,10,10,0.75)",
                    border: lightsOn ? "1px solid #ff8f8a" : "1px solid #262626",
                    borderRadius: 4,
                    backdropFilter: "blur(10px)",
                    letterSpacing: "0.16em",
                    boxShadow: lightsOn
                        ? "0 6px 18px rgba(225,6,0,0.4)"
                        : "0 4px 12px rgba(0,0,0,0.5)",
                    fontFamily: "'JetBrains Mono', monospace",
                }}
                aria-label="Toggle lights"
            >
                <Lightbulb className="w-3.5 h-3.5" strokeWidth={2.4} />
                {lightsOn ? "LIGHTS ON" : "LIGHTS OFF"}
            </motion.button>
        </div>
    );
}
